import React from 'react';
import { t } from '@lingui/macro';
import { useToast } from '@economic/taco';

import { CompanyResponse } from '~/app/pages/accounting/payment-cards/type';
import { useFancyState } from '../useFancyState';
import { PaymentCardsApi } from '../../api';
import { getStepConfig } from './steps/config';
import { openNewWindowWithPromise } from '../windowUtils';
import { type CompanyInformation, OnboardingStepEnum, type PaymentCardsOnboardingDialogState } from './types';

export interface UsePaymentCardsOnboardingDialogContextBuilderArgs {
    api: PaymentCardsApi;
    agreementNumber: number;
    walletUrl: string;
    onCloseDialog: () => void;
}

const getInitFormState = (): PaymentCardsOnboardingDialogState['formData'] => ({
    companyName: '',
    country: '',
    cvr: '',
    agreeWithTerms: false,
});

const getFormState = (companyData?: CompanyResponse) => ({
    companyName: companyData?.model.name || '',
    country: companyData?.model.country || '',
    cvr: companyData?.model.cvr || '',
});

export const usePaymentCardsOnboardingDialogContextBuilder = ({
    api,
    agreementNumber,
    walletUrl,
    onCloseDialog,
}: UsePaymentCardsOnboardingDialogContextBuilderArgs) => {
    const toast = useToast();

    const { state, setState, setMultipleState } = useFancyState<PaymentCardsOnboardingDialogState>({
        agreementNumber,
        companyData: getFormState(),
        formData: getInitFormState(),
        isLoading: false,
        countries: [],
        step: OnboardingStepEnum.STEP1,
        walletUrl,
    });

    const stepperConfig = React.useMemo(() => getStepConfig({ api, onClose: onCloseDialog }), [api, onCloseDialog]);

    const currentConfig = stepperConfig.getStepConfig(state.step);

    const onContinue = React.useCallback(async () => {
        const currentStep = stepperConfig.getStepConfig(state.step);
        if (currentStep === undefined) {
            console.error(`Invalid step for onContinue: ${state.step}`);
            return;
        }

        setMultipleState({ isLoading: true });
        const nextStepPartialStateObject = await currentStep.next?.stateOnClick(state).catch(null);
        if (!nextStepPartialStateObject) {
            return;
        }

        const { error, success, redirectUrl, ...partialStateObject } = nextStepPartialStateObject;
        if (error) {
            toast.error(error, { autoClose: 4000 });
        }

        if (success) {
            toast.success(success, { autoClose: 4000 });
        }

        setMultipleState({ ...partialStateObject, isLoading: false });

        if (redirectUrl) {
            // wait a bit before the redirect
            setTimeout(() => {
                location.href = redirectUrl;
            }, 1000);
        }
    }, [stepperConfig, state, setMultipleState, toast]);

    const onBack = React.useCallback(async () => {
        const currentStep = stepperConfig.getStepConfig(state.step)?.prev;
        if (!currentStep) {
            return;
        }
        const partialStateObject = await currentStep.stateOnClick(state);
        if (partialStateObject !== null) {
            setMultipleState(partialStateObject);
            const { redirectUrl } = partialStateObject;
            if (redirectUrl) {
                // wait a bit before the redirect
                setTimeout(() => {
                    location.href = redirectUrl;
                }, 1000);
            }
        }
    }, [state, stepperConfig, setMultipleState]);

    const updateCompanyInfo = React.useCallback(
        (value: unknown, name: keyof CompanyInformation) => {
            setState('formData', {
                ...state.formData,
                [name]: value,
            });
        },
        [state, setState]
    );

    const loadCompanyData = React.useCallback(async () => {
        try {
            const company = await api.getCurrentCompany();
            setMultipleState({
                formData: {
                    ...state.formData,
                    ...getFormState(company),
                },
                companyData: getFormState(company),
            });
        } catch (err: unknown) {
            toast.error(t`Failed to load the company information`, { autoClose: 4000 });
        }
    }, [api, setMultipleState, state.formData, toast]);

    // const activateResponse = await internalApiClient.post('paymentcard/developer/company');
    const openMasterSettings = React.useCallback(() => {
        openNewWindowWithPromise('/settings/company/master-information').then(loadCompanyData).catch(null);
    }, [loadCompanyData]);

    // load the initially required data for the 2nd step
    React.useEffect(() => {
        loadCompanyData();
    }, []);

    return {
        state,
        config: stepperConfig.steps,
        currentConfig,

        // data changer actions
        updateCompanyInfo,
        openMasterSettings,

        // ui related actions
        onContinue,
        onBack,
        onCloseDialog,
    };
};
